import classNames from 'classnames'
import { FC, useContext, useEffect, useState, useRef, createRef } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Link } from 'components'
import { ERoutePaths } from 'libraries/router/types'
import { useAppSelector, useAppDispatch } from 'libraries/redux'
import { CategoriesSelectors } from 'store/category/selectors'
import { getCategories } from 'store/category/actions'
import { useResponsive } from 'shared/hooks'
import { PhoneIcon, HearthIcon, SearchIcon, ShoppingCartIcon, MenuIcon } from 'shared/assets/icons'
import Badge, { badgeClasses } from '@mui/material/Badge'
import styles from './Header.module.scss'
import { SupplierContext } from 'context/SupplierContext'
import { useGetFavoriteMerchantQuery } from '../../../entities/Favorites/api/apiFavoritesSlice'
import { useFetchCategoriesListQuery } from '../../../entities/Categories/api/supplier/apiCategoriesSlice'
import { styled } from '@mui/material'
import { useGetCartItemsQuery } from '../../../entities/Order/api/apiCartSlice'
import { getSocialUrls } from 'shared/lib/utils/socialLinks/socialLinks'
import BurgerMenu from 'features/Header/ui/Burder/BurgerMenu'
import { useCartDrawer } from 'context/BasketDriverContext/BasketDriverContext'
import { useBurgerMenu } from 'context/BurgerMenuContext/BurgerMenuContext'
import { useWishlistDrawer } from 'context/WishlistDrawerContext/WishlistDrawerContext'
import SubcategoryDropdown from 'features/Header/ui/SubcategoryDropdown/SubcategoryDropdown'

const Header: FC<any> = ({ setSearch, isSearch }: any) => {
  const location = useLocation()
  const isCheckoutPage = location.pathname === '/basket' || location.pathname === '/order-success'
  const CartBadge = styled(Badge)`
    & .${badgeClasses.badge} {
      top: -8px;
      right: 2px;
      width: 18px !important;
      height: 18px !important;
      font-size: 8px;
      background: rgb(0, 0, 0);
    }
  `

  const { isTabletL } = useResponsive()
  const { is_supplier } = useContext(SupplierContext)
  const { openDrawer } = useCartDrawer()
  const { openDrawer: openWishlistDrawer } = useWishlistDrawer()
  const { openMenu } = useBurgerMenu()
  const dispatch = useAppDispatch()

  const customerId = Number(window.config.CUSTOMER_ID)
  const categoryId = Number(window.config.CATEGORY_ID)
  const queryParam = is_supplier ? customerId : 0

  const { data: storeInfo } = useAppSelector(CategoriesSelectors.storeInfo)
  const { data: categoriesList } = useAppSelector(CategoriesSelectors.categoriesList)
  const { data: forTitle, isLoading: forTitleLoading } = useFetchCategoriesListQuery(
    {
      customer_id: customerId,
      parent_id: categoryId,
      is_supplier: is_supplier,
    },
    { skip: !is_supplier || !customerId }
  )

  // Fallback запрос без filter_parent_id если первый запрос вернул пустой массив
  const shouldFallback = is_supplier && !forTitleLoading && forTitle?.data?.length === 0
  const { data: fallbackCategories } = useFetchCategoriesListQuery(
    {
      customer_id: customerId,
      parent_id: null,
      is_supplier: is_supplier,
    },
    { skip: !shouldFallback }
  )

  const { data: favoritesProducts } = useGetFavoriteMerchantQuery({merchant_id: queryParam})
  const { data: cartItems, refetch } = useGetCartItemsQuery({
    merchant_id: queryParam,
    per_page: 20,
    page: 1,
  })

  // Get top 5 categories
  const allCategories = is_supplier
    ? (forTitle?.data?.length > 0 ? forTitle?.data : fallbackCategories?.data)
    : categoriesList
  const topFiveCategories = allCategories?.slice(0, 5) || []

  const socialUrls = getSocialUrls(storeInfo)
  const hasSocialUrls = socialUrls?.some(({ url }) => url !== null)

  const [openSubcategoryId, setOpenSubcategoryId] = useState<number | null>(null)
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | null>(null)
  const categoryRefs = useRef<{ [key: number]: React.RefObject<HTMLDivElement> }>({})

  const openSubcategoryDropdown = (categoryId: number) => {
    if (closeTimeout) {
      clearTimeout(closeTimeout)
      setCloseTimeout(null)
    }

    // setIsCategoryDropdownOpen(false)
    setOpenSubcategoryId(categoryId)
  }

  const closeSubcategoryDropdown = () => {
    if (closeTimeout) {
      clearTimeout(closeTimeout)
    }
    const timeout = setTimeout(() => {
      setOpenSubcategoryId(null)
    }, 300)
    setCloseTimeout(timeout)
  }

  useEffect(() => {
    refetch()
  }, [cartItems, refetch])

  useEffect(() => {
    if (!is_supplier) {
      dispatch(getCategories(window.config.CATEGORY_ID))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_supplier])

  useEffect(() => {
    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout)
      }
    }
  }, [closeTimeout])

  const trackFbqEvent = (event: 'Contact' | 'Search') => {
    if (storeInfo?.fb_pixel_id && typeof window.fbq === 'function') {
      window.fbq('track', event)
    }
  }

  const trackGAViewCartEvent = () => {
    if (typeof window.gtag !== 'function' || !cartItems?.data?.length) return

    window.gtag('event', 'view_cart', {
      currency: 'UAH',
      value: cartItems?.total,
      items: cartItems?.data.map((item: any) => ({
        item_id: item.product_id,
        item_name: item.name,
        quantity: item.quantity,
        price: item.price,
      })),
    })
  }

  const renderMenuList = (
    <>
      <div className={styles.wrapper__menu_item}>
        <Link
          className={styles.wrapper__menu_item_link}
          navigateTo={ERoutePaths.Catalog}
        >
          Каталог
        </Link>
      </div>
      {topFiveCategories?.map((category: any) => {
        if (!categoryRefs.current[category.id]) {
          categoryRefs.current[category.id] = createRef<HTMLDivElement>()
        }

        return (
          <div
            key={category.id}
            ref={categoryRefs.current[category.id]}
            className={styles.wrapper__menu_item}
            onMouseEnter={() => openSubcategoryDropdown(category.id)}
            onMouseLeave={closeSubcategoryDropdown}
          >
            <Link
              className={styles.wrapper__menu_item_link}
              navigateTo={`/products?categoryId=${category.id}`}
            >
              {category.name}
            </Link>
            <SubcategoryDropdown
              category={category}
              isOpen={openSubcategoryId === category.id}
              onClose={closeSubcategoryDropdown}
              onMouseEnter={() => openSubcategoryDropdown(category.id)}
              parentRef={categoryRefs.current[category.id]}
            />
          </div>
        )
      })}
    </>
  )

  const renderMenuSocList = socialUrls?.map(({ url, Icon }, index) => {
    if (!url) return null

    const handleClick = () => {
      if (!isTabletL || index === 0) {
        trackFbqEvent('Contact')
      }
    }

    return (
      <a
        key={index}
        href={url}
        target='_blank'
        onClick={handleClick}
        className={styles.wrapper_container__menu_list_items_name}
      >
        <Icon />
      </a>
    )
  })

  return (
    <>
      <header className={classNames(styles.container, {
        [styles.container_active]: openSubcategoryId !== null && !isCheckoutPage,
      })}>
        <div className={classNames(styles.wrapper, 'container', {
          [styles.wrapper_checkout]: isCheckoutPage,
        })}>
          <div className={styles.wrapper__left}>
            {/* Only show navigation on non-checkout pages */}
            {!isCheckoutPage && isTabletL && (
              <div className={styles.burger}>
                <div onClick={() => openMenu()} style={{ width: 25, height: 25 }}>
                  <MenuIcon style={{ width: 25, height: 25 }} />
                </div>
              </div>
            )}

            {/* Logo */}
            <NavLink to={ERoutePaths.Home} className={styles.wrapper__logo}>
              <p>{storeInfo?.name}</p>
            </NavLink>
          </div>

          {/* Only show navigation on non-checkout pages */}
          {!isCheckoutPage && (
            <>
              {!isTabletL && (
                <div className={styles.wrapper__menu}>
                  <nav className={styles.wrapper__menu_nav}>
                    {renderMenuList}
                  </nav>
                </div>
              )}

              <BurgerMenu
                isSupplier={is_supplier}
                renderMenuListForSuppliers={renderMenuList}
                renderMenuList={renderMenuList}
                isTabletL={isTabletL}
              />

              <div className={styles.wrapper__settings}>
                <div
                  className={styles.wrapper__settings_item}
                  onClick={() => openWishlistDrawer()}
                >
                  <HearthIcon />
                  <CartBadge badgeContent={favoritesProducts?.meta?.total} color='primary' overlap='circular' />
                </div>
                <div
                    className={styles.wrapper__settings_item}
                    onClick={() => {
                      setSearch(true), window.scrollTo({ top: 0, behavior: 'smooth' })
                    }}
                >
                  <SearchIcon />
                </div>
                <div
                  className={styles.wrapper__settings_item}
                  onClick={() => {
                    trackGAViewCartEvent()
                    openDrawer()
                  }}
                >
                  <ShoppingCartIcon />
                  <CartBadge badgeContent={cartItems?.meta?.total} color='primary' />
                </div>
              </div>
            </>
          )}

          {/* Only show basket icon on checkout pages */}
          {isCheckoutPage && (
            <div className={styles.wrapper__settings}>
              <div
                className={styles.wrapper__settings_item}
                onClick={() => {
                  trackGAViewCartEvent()
                  openDrawer()
                }}
              >
                <ShoppingCartIcon />
                <CartBadge badgeContent={cartItems?.meta?.total} color='primary' />
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  )
}

export default Header
